import React, { useState, useEffect, useRef } from "react";
import { useNode, useEditor } from "@craftjs/core";
import { FormControl, FormLabel, Slider, TextField, Select, MenuItem, Checkbox, FormControlLabel, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { useTemplateString } from './useTemplateString';
import { useTextPersistence } from "qwanyx";

export const Text = ({
  name,
  text,
  fontSize,
  fontFamily = 'Arial',
  fontWeight = 'normal',
  fontStyle = 'normal',
  textAlign = 'left',
  color = '#000000',
  position = 'relative',
  top,
  left,
  right,
  bottom,
  unit = 'px',
  lockedEdges = { top: false, left: false, right: false, bottom: false },
  backgroundColor,
  borderRadius,
  padding,
  cssClass,
  qwantumId,
  onSave,
  style,
  hidden,
  ...props
}) => {
  const { 
    connectors: { connect, drag }, 
    hasSelectedNode,
    isSelected,
    isHovered,
    actions: { setProp },
    parent
  } = useNode((state) => ({
    hasSelectedNode: state.events.selected,
    isSelected: state.events.selected,
    isHovered: state.events.hovered,
    parent: state.data.parent
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled
  }));

  // Hook pour template strings
  const { processTemplateString, hasContext } = useTemplateString();

  // Persistence hook - only use if qwantumId is provided
  const { text: persistedText, handleBlur: persistenceBlur } = useTextPersistence(qwantumId, onSave);
  const [prevValue, setPrevValue] = useState(text || persistedText || "");

  const [editable, setEditable] = useState(false);
  const textRef = useRef(null);
  const cursorPos = useRef(0);

  useEffect(() => {
    !hasSelectedNode && setEditable(false);
  }, [hasSelectedNode]);

  if (hidden) return null; 

  // Use persisted text if qwantumId exists (empty if no persisted text), otherwise use prop text
  const sourceText = qwantumId ? (persistedText || "") : text;
  
  // Traitement automatique des template strings
  const displayText = processTemplateString(sourceText);

  const saveCursorPosition = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      cursorPos.current = range.startOffset;
    }
  };

  const restoreCursorPosition = () => {
    if (textRef.current && editable) {
      const selection = window.getSelection();
      const range = document.createRange();
      const textNode = textRef.current.firstChild;
      
      if (textNode) {
        const pos = Math.min(cursorPos.current, textNode.textContent.length);
        range.setStart(textNode, pos);
        range.setEnd(textNode, pos);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  const handleInput = (e) => {
    saveCursorPosition();
    setProp(props => props.text = e.target.textContent);
    setTimeout(restoreCursorPosition, 0);
  };

  const handleBlur = (e) => {
    setEditable(false);
    
    // Handle persistence if qwantumId is provided
    if (qwantumId) {
      const newValue = e.target.textContent;
      
      // Only persist if value has changed
      if (newValue !== prevValue) {
        // Create a synthetic event for persistenceBlur
        const syntheticEvent = {
          target: { value: newValue }
        };
        persistenceBlur(syntheticEvent);
        
        if (onSave) {
          onSave(newValue);
        }
        setPrevValue(newValue);
      }
    }
  };

  const getSelectionStyle = () => {
    if (isSelected && !editable) {
      return {
        outline: '1px dashed #2196f3',
        outlineOffset: '2px'
      };
    }
    if (isHovered && !editable) {
      return {
        outline: '1px dashed #ff9800',
        outlineOffset: '1px'
      };
    }
    return {};
  };

  const getPositionStyle = () => {
    if (position === 'absolute' || position === 'relative') {
      const positionStyle = { position };
      
      if (lockedEdges.top && top !== undefined) {
        positionStyle.top = `${top}${unit}`;
      }
      if (lockedEdges.left && left !== undefined) {
        positionStyle.left = `${left}${unit}`;
      }
      if (lockedEdges.right && right !== undefined) {
        positionStyle.right = `${right}${unit}`;
      }
      if (lockedEdges.bottom && bottom !== undefined) {
        positionStyle.bottom = `${bottom}${unit}`;
      }
      
      return positionStyle;
    }
    return { position };
  };

  return (
    <div 
      ref={ref => connect(drag(ref))}
      name={name}
      className={cssClass}
      onClick={() => enabled && setEditable(true)}
      style={{
        transition: 'all 0.2s ease',
        display: 'inline-block',
        backgroundColor: backgroundColor,
        borderRadius: borderRadius ? `${borderRadius}px` : undefined,
        padding: padding ? `${padding}px` : undefined,
        ...getPositionStyle(),
        ...style,
        ...getSelectionStyle()
      }}
      {...props}
    >
      <p
        ref={textRef}
        contentEditable={editable && enabled}
        suppressContentEditableWarning={true}
        onInput={handleInput}
        onBlur={handleBlur}
        style={{ 
          fontSize: `${fontSize}px`,
          fontFamily: fontFamily,
          fontWeight: fontWeight,
          fontStyle: fontStyle,
          textAlign: textAlign,
          color: color,
          outline: editable ? '1px dashed #ccc' : 'none',
          margin: 0,
          padding: 0
        }}
      >
        {displayText}
      </p>
    </div>
  );
};

const TextSettings = () => {
  const { 
    actions: { setProp }, 
    name, text, fontSize, fontFamily, fontWeight, fontStyle, textAlign, color,
    position, top, left, right, bottom, unit, lockedEdges,
    backgroundColor, borderRadius, padding, cssClass, qwantumId,
    parent
  } = useNode((node) => ({
    name: node.data.props.name,
    text: node.data.props.text,
    fontSize: node.data.props.fontSize,
    fontFamily: node.data.props.fontFamily,
    fontWeight: node.data.props.fontWeight,
    fontStyle: node.data.props.fontStyle,
    textAlign: node.data.props.textAlign,
    color: node.data.props.color,
    position: node.data.props.position,
    top: node.data.props.top,
    left: node.data.props.left,
    right: node.data.props.right,
    bottom: node.data.props.bottom,
    unit: node.data.props.unit,
    lockedEdges: node.data.props.lockedEdges,
    backgroundColor: node.data.props.backgroundColor,
    borderRadius: node.data.props.borderRadius,
    padding: node.data.props.padding,
    cssClass: node.data.props.cssClass,
    qwantumId: node.data.props.qwantumId,
    parent: node.data.parent
  }));

  // Hook pour détecter si on est dans ListCraft
  const { hasContext, itemContext } = useTemplateString();

  const { query } = useEditor();
  const parentNode = parent ? query.node(parent).get() : null;

  return (
    <div>
      {parentNode && (
        <FormControl fullWidth margin="normal">
          <FormLabel>Parent: {parentNode.data.displayName || parentNode.data.name || 'Container'}</FormLabel>
          {unit === '%' && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              Note: Parent needs position:relative and defined dimensions for % to work
            </div>
          )}
        </FormControl>
      )}
      
      <FormControl fullWidth margin="normal">
        <FormLabel>Component Name</FormLabel>
        <TextField
          value={name || ''}
          onChange={(e) => setProp(props => props.name = e.target.value)}
          size="small"
          placeholder="Enter component name"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Quantum ID</FormLabel>
        <TextField
          value={qwantumId || ''}
          onChange={(e) => setProp(props => props.qwantumId = e.target.value)}
          size="small"
          placeholder="Enter quantum ID for persistence"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Text Content</FormLabel>
        <TextField
          value={text || ''}
          onChange={(e) => setProp(props => props.text = e.target.value)}
          size="small"
          multiline
          rows={2}
          placeholder={hasContext ? "Use ${item.name}, ${item.title}, ${item.description}..." : "Enter text"}
        />
        {hasContext && (
          <div style={{ 
            fontSize: '12px', 
            color: '#2196f3', 
            marginTop: '4px',
            padding: '8px',
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
            borderRadius: '4px',
            border: '1px solid rgba(33, 150, 243, 0.3)'
          }}>
            💡 <strong>Template strings available:</strong><br/>
            <code>${"{item.name}"}</code>, <code>${"{item.title}"}</code>, <code>${"{item.description}"}</code>, <code>${"{item.status}"}</code>, <code>${"{index}"}</code>
            {itemContext && (
              <div style={{ marginTop: '4px' }}>
                <strong>Current item:</strong> {JSON.stringify(itemContext.item, null, 2).substring(0, 100)}...
              </div>
            )}
          </div>
        )}
      </FormControl>

      <FormControl size="small" component="fieldset" fullWidth margin="normal">
        <FormLabel component="legend">Font Size</FormLabel>
        <Slider
          value={fontSize || 16}
          step={1}
          min={8}
          max={72}
          valueLabelDisplay="auto"
          onChange={(_, value) => {
            setProp(props => props.fontSize = value);
          }}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Font Family</FormLabel>
        <Select
          value={fontFamily || 'Arial'}
          onChange={(e) => setProp(props => props.fontFamily = e.target.value)}
          size="small"
        >
          <MenuItem value="Arial">Arial</MenuItem>
          <MenuItem value="Helvetica">Helvetica</MenuItem>
          <MenuItem value="Montserrat">Montserrat</MenuItem>
          <MenuItem value="Times New Roman">Times New Roman</MenuItem>
          <MenuItem value="Georgia">Georgia</MenuItem>
          <MenuItem value="Verdana">Verdana</MenuItem>
          <MenuItem value="Courier New">Courier New</MenuItem>
          <MenuItem value="Comic Sans MS">Comic Sans MS</MenuItem>
          <MenuItem value="Impact">Impact</MenuItem>
          <MenuItem value="Trebuchet MS">Trebuchet MS</MenuItem>
          <MenuItem value="Arial Black">Arial Black</MenuItem>
        </Select>
      </FormControl>

      <FormControlLabel
        control={
          <Checkbox
            checked={fontWeight === 'bold'}
            onChange={(e) => setProp(props => props.fontWeight = e.target.checked ? 'bold' : 'normal')}
          />
        }
        label="Bold"
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={fontStyle === 'italic'}
            onChange={(e) => setProp(props => props.fontStyle = e.target.checked ? 'italic' : 'normal')}
          />
        }
        label="Italic"
      />

      <FormControl fullWidth margin="normal">
        <FormLabel>Text Alignment</FormLabel>
        <Select
          value={textAlign || 'left'}
          onChange={(e) => setProp(props => props.textAlign = e.target.value)}
          size="small"
        >
          <MenuItem value="left">Left</MenuItem>
          <MenuItem value="center">Center</MenuItem>
          <MenuItem value="right">Right</MenuItem>
          <MenuItem value="justify">Justify</MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal" component="fieldset">
        <FormLabel component="legend">Text Color</FormLabel>
        <input
          type="color"
          value={color || '#000000'}
          onChange={(e) => setProp(props => props.color = e.target.value)}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>CSS Class</FormLabel>
        <TextField
          value={cssClass || ''}
          onChange={(e) => setProp(props => props.cssClass = e.target.value)}
          size="small"
          placeholder="Enter CSS class names"
        />
      </FormControl>

      <FormControl fullWidth margin="normal" component="fieldset">
        <FormLabel component="legend">Background Color</FormLabel>
        <input
          type="color"
          value={backgroundColor || '#ffffff'}
          onChange={(e) => setProp(props => props.backgroundColor = e.target.value)}
        />
      </FormControl>

      <FormControl size="small" component="fieldset" fullWidth margin="normal">
        <FormLabel component="legend">Border Radius</FormLabel>
        <Slider
          value={borderRadius || 0}
          step={1}
          min={0}
          max={50}
          valueLabelDisplay="auto"
          onChange={(_, value) => {
            setProp(props => props.borderRadius = value);
          }}
        />
      </FormControl>

      <FormControl size="small" component="fieldset" fullWidth margin="normal">
        <FormLabel component="legend">Padding</FormLabel>
        <Slider
          value={padding || 0}
          step={1}
          min={0}
          max={50}
          valueLabelDisplay="auto"
          onChange={(_, value) => {
            setProp(props => props.padding = value);
          }}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Position</FormLabel>
        <Select
          value={position || 'relative'}
          onChange={(e) => setProp(props => props.position = e.target.value)}
          size="small"
        >
          <MenuItem value="relative">Relative</MenuItem>
          <MenuItem value="absolute">Absolute</MenuItem>
        </Select>
      </FormControl>

      {(position === 'absolute' || position === 'relative') && (
        <>
          <FormControl fullWidth margin="normal">
            <FormLabel>Unit</FormLabel>
            <ToggleButtonGroup
              value={unit}
              exclusive
              onChange={(e, value) => value && setProp(props => props.unit = value)}
              size="small"
            >
              <ToggleButton value="px">px</ToggleButton>
              <ToggleButton value="%">%</ToggleButton>
            </ToggleButtonGroup>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Position Controls {position === 'relative' ? '(relative to container)' : '(absolute)'}</FormLabel>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={lockedEdges?.top || false}
                    onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, top: e.target.checked })}
                  />
                }
                label="Top"
              />
              <TextField
                value={top || ''}
                onChange={(e) => setProp(props => props.top = parseFloat(e.target.value) || 0)}
                size="small"
                type="number"
                disabled={!lockedEdges?.top}
                style={{ width: '80px' }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={lockedEdges?.left || false}
                    onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, left: e.target.checked })}
                  />
                }
                label="Left"
              />
              <TextField
                value={left || ''}
                onChange={(e) => setProp(props => props.left = parseFloat(e.target.value) || 0)}
                size="small"
                type="number"
                disabled={!lockedEdges?.left}
                style={{ width: '80px' }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={lockedEdges?.right || false}
                    onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, right: e.target.checked })}
                  />
                }
                label="Right"
              />
              <TextField
                value={right || ''}
                onChange={(e) => setProp(props => props.right = parseFloat(e.target.value) || 0)}
                size="small"
                type="number"
                disabled={!lockedEdges?.right}
                style={{ width: '80px' }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={lockedEdges?.bottom || false}
                    onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, bottom: e.target.checked })}
                  />
                }
                label="Bottom"
              />
              <TextField
                value={bottom || ''}
                onChange={(e) => setProp(props => props.bottom = parseFloat(e.target.value) || 0)}
                size="small"
                type="number"
                disabled={!lockedEdges?.bottom}
                style={{ width: '80px' }}
              />
            </div>
          </FormControl>
        </>
      )}
    </div>
  );
};

Text.craft = {
  props: {
    name: "",
    text: "Hi",
    fontSize: 20,
    fontFamily: 'Arial',
    fontWeight: 'normal',
    fontStyle: 'normal',
    textAlign: 'left',
    color: '#000000',
    position: 'relative',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    unit: 'px',
    lockedEdges: { top: false, left: false, right: false, bottom: false },
    backgroundColor: '',
    borderRadius: 0,
    padding: 0,
    cssClass: '',
    qwantumId: ''
  },
  rules: {
    canDrag: (node) => node.data.props.text !== "Drag"
  },
  related: {
    settings: TextSettings
  }
};